import { useEffect, useMemo, useState } from 'react';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  AsYouType,
  type CountryCode,
} from 'libphonenumber-js';
import { phoneInputMobileProps } from '../lib/mobileInput';
import { contextFieldInputClass } from './ui/ContextTypography';

const DEFAULT_COUNTRY: CountryCode = 'PK';
const PK_NATIONAL_DIGITS = 10;

function sortCountries(countries: CountryCode[]): CountryCode[] {
  const rest = countries.filter((c) => c !== 'PK').sort((a, b) => {
    try {
      const nameA = new Intl.DisplayNames(['en'], { type: 'region' }).of(a) ?? a;
      const nameB = new Intl.DisplayNames(['en'], { type: 'region' }).of(b) ?? b;
      return nameA.localeCompare(nameB);
    } catch {
      return a.localeCompare(b);
    }
  });
  return ['PK', ...rest];
}

const ALL_COUNTRIES = sortCountries(getCountries() as CountryCode[]);

function countryLabel(code: CountryCode): string {
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code;
    return `${name} (+${getCountryCallingCode(code)})`;
  } catch {
    return `${code} (+${getCountryCallingCode(code)})`;
  }
}

function parseNationalNumber(phone: string | undefined, country: CountryCode): string {
  if (!phone) return '';
  const trimmed = phone.trim();
  if (!trimmed) return '';

  const parsed = trimmed.startsWith('+')
    ? parsePhoneNumberFromString(trimmed)
    : parsePhoneNumberFromString(trimmed, country);

  if (parsed?.isValid()) {
    return parsed.nationalNumber;
  }

  return trimmed.replace(/\D/g, '');
}

export interface PhoneInputProps {
  phone: string;
  phoneCountry: string;
  onChange: (phone: string, phoneCountry: string) => void;
  error?: string;
  variant?: 'onboarding' | 'settings' | 'context';
  disabled?: boolean;
}

export default function PhoneInput({
  phone,
  phoneCountry,
  onChange,
  error,
  variant = 'onboarding',
  disabled = false,
}: PhoneInputProps) {
  const country = ((phoneCountry || DEFAULT_COUNTRY).toUpperCase() as CountryCode) || DEFAULT_COUNTRY;
  const [national, setNational] = useState(() => parseNationalNumber(phone, country));

  useEffect(() => {
    setNational(parseNationalNumber(phone, country));
  }, [phone, country]);

  const displayNational = useMemo(() => {
    if (!national) return '';
    if (country === 'PK') {
      return national;
    }
    try {
      return new AsYouType(country).input(national);
    } catch {
      return national;
    }
  }, [national, country]);

  const handleCountryChange = (nextCountry: string) => {
    const code = nextCountry.toUpperCase() as CountryCode;
    if (!national) {
      onChange('', code);
      return;
    }
    const parsed = parsePhoneNumberFromString(national, code);
    onChange(parsed?.isValid() ? parsed.format('E.164') : national, code);
  };

  const handleNationalChange = (raw: string) => {
    let digits = raw.replace(/\D/g, '');
    if (country === 'PK') {
      digits = digits.slice(0, PK_NATIONAL_DIGITS);
    }
    setNational(digits);

    if (!digits) {
      onChange('', country);
      return;
    }

    if (country === 'PK') {
      onChange(digits.length === PK_NATIONAL_DIGITS ? `+92${digits}` : digits, country);
      return;
    }

    const parsed = parsePhoneNumberFromString(digits, country);
    if (parsed?.isValid()) {
      onChange(parsed.format('E.164'), country);
    } else {
      onChange(digits, country);
    }
  };

  const isOnboarding = variant === 'onboarding';
  const isContext = variant === 'context';

  const selectClass = isOnboarding
    ? 'bg-white/[0.06] border border-white/10 text-white text-base md:text-lg rounded-xl px-3 py-3 outline-none focus:border-[#C5CED9]/50 w-full sm:max-w-[48%] shrink-0 backdrop-blur-md'
    : isContext
      ? `${contextFieldInputClass} shrink-0 sm:max-w-[220px]`
      : 'w-full sm:max-w-[220px] px-4 py-2 border border-border-light rounded-xl focus:ring-2 focus:ring-primary outline-none bg-white text-[#0F172A]';

  const inputClass = isOnboarding
    ? 'flex-1 min-w-0 text-2xl md:text-3xl bg-transparent border-b-2 border-white/10 focus:border-[#C5CED9] py-3 outline-none placeholder:text-white/15 font-medium transition-all duration-300 text-white focus:shadow-[0_1px_0_0_rgba(197,206,217,0.4)]'
    : isContext
      ? `${contextFieldInputClass} flex-1 min-w-0`
      : 'flex-1 min-w-0 px-4 py-2 border border-border-light rounded-xl focus:ring-2 focus:ring-primary outline-none';

  const errorClass = isOnboarding ? 'text-red-400 text-sm mt-2' : 'text-red-600 text-sm mt-1';

  return (
    <div>
      <div className={isOnboarding ? 'flex flex-col sm:flex-row gap-4' : 'flex flex-col sm:flex-row gap-3'}>
        <select
          value={country}
          onChange={(e) => handleCountryChange(e.target.value)}
          disabled={disabled}
          className={selectClass}
          aria-label="Country code"
        >
          {ALL_COUNTRIES.map((c) => (
            <option key={c} value={c} className="text-[#0F172A]">
              {countryLabel(c)}
            </option>
          ))}
        </select>
        <input
          {...phoneInputMobileProps}
          data-onboarding-focus={isOnboarding ? true : undefined}
          disabled={disabled}
          maxLength={country === 'PK' ? PK_NATIONAL_DIGITS : undefined}
          className={`${inputClass} ${error ? (isOnboarding ? 'border-red-400/80' : 'border-red-400') : ''}`}
          placeholder={country === 'PK' ? '3001234567' : 'Phone number'}
          value={displayNational}
          onChange={(e) => handleNationalChange(e.target.value)}
        />
      </div>
      {error && <p className={errorClass}>{error}</p>}
    </div>
  );
}
